import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosRequestConfig } from 'axios';
import { API_CONFIG, AUTH_CONFIG } from '../config/config';
import { ApiResponse } from '../types/api';

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop();
    if (cookieValue) {
      return cookieValue.split(';').shift() || null;
    }
  }
  return null;
};

const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];

const authUtils = {
  clearTokens: () => {
    localStorage.removeItem('businessSetupCompleted');
    localStorage.removeItem('businessDetails');
    localStorage.removeItem('processedAuthResponses');
    localStorage.removeItem('invoiceData');
    localStorage.removeItem('invoiceDisplayPreferences');
    sessionStorage.removeItem('preAuthPath');
  },
  redirectToLogin: () => {
    window.location.href = AUTH_CONFIG.ROUTES.LOGIN;
  },
  redirectToMaintenance: () => {
    if (window.location.pathname !== AUTH_CONFIG.ROUTES.MAINTENANCE) {
      window.location.replace(AUTH_CONFIG.ROUTES.MAINTENANCE);
    }
  }
};

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const refreshToken = async (): Promise<boolean> => {
  const baseUrl = API_CONFIG.BASE_URL;
  const response = await axios.post<ApiResponse<any>>(
    `${baseUrl}${API_CONFIG.ENDPOINTS.AUTH.REFRESH_TOKEN}`,
    {},
    { withCredentials: true }
  );
  return response.data?.success === true;
};

interface CustomAxiosInstance extends AxiosInstance {
  get<T = any, R = ApiResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>;
  post<T = any, R = ApiResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R>;
  put<T = any, R = ApiResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R>;
  delete<T = any, R = ApiResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>;
  clearAuthTokens: () => void;
}

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  withCredentials: true,
}) as unknown as CustomAxiosInstance;

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const method = config.method?.toUpperCase() || 'GET';
  if (!SAFE_METHODS.includes(method)) {
    const csrfToken = getCookie('XSRF-TOKEN');
    if (csrfToken && config.headers) {
      config.headers[API_CONFIG.HEADERS.XSRF_TOKEN] = csrfToken;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },
  async (error: any) => {
    const originalRequest = error.config;

    const isMeEndpoint = originalRequest?.url?.includes('/api/auth/me');

    if (!error.response) {
      if (isMeEndpoint) {
        return Promise.reject(error);
      }
      if ((originalRequest as any)?.skipMaintenanceRedirect) {
        return Promise.reject(new Error('Backend server is unavailable.'));
      }
      authUtils.clearTokens();
      if (import.meta.env.DEV) {
        console.warn('Backend unreachable, redirecting to maintenance');
      }
      authUtils.redirectToMaintenance();
      return Promise.reject(new Error('Backend server is unavailable.'));
    }

    if (isMeEndpoint) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => {
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      let refreshError: unknown;
      try {
        const refreshed = await refreshToken();
        isRefreshing = false;
        if (refreshed) {
          onRefreshed('refreshed');
          return api(originalRequest);
        }
      } catch (error) {
        refreshError = error;
        isRefreshing = false;
        refreshSubscribers = [];
      }

      isRefreshing = false;
      refreshSubscribers = [];
      authUtils.clearTokens();
      authUtils.redirectToLogin();
      return Promise.reject(refreshError || new Error('Authentication failed'));
    }

    const apiResponse = error.response?.data as ApiResponse<any>;
    return Promise.reject(apiResponse?.message || error.message);
  }
);

api.clearAuthTokens = authUtils.clearTokens;

export default api;
